// src/components/common/SkeletonCard.tsx
import React from 'react';
import SkeletonLoader from './SkeletonLoader';
import './SkeletonCard.scss';

interface SkeletonCardProps {
  type?: 'appointment' | 'stylist' | 'service' | 'notification' | 'generic';
  className?: string;
}

/**
 * Pre-configured skeleton cards for different content types
 */
const SkeletonCard: React.FC<SkeletonCardProps> = ({ type = 'generic', className = '' }) => {
  switch (type) {
    case 'appointment':
      return (
        <div className={`skeleton-card ${className}`}>
          <div className="skeleton-card__header">
            <SkeletonLoader width={60} height={60} borderRadius="50%" />
            <div className="skeleton-card__header-text">
              <SkeletonLoader width="80%" height={18} />
              <SkeletonLoader width="60%" height={14} style={{ marginTop: 8 }} />
            </div>
          </div>
          <div className="skeleton-card__body">
            <SkeletonLoader width="100%" height={14} />
            <SkeletonLoader width="90%" height={14} style={{ marginTop: 8 }} />
            <SkeletonLoader width="70%" height={14} style={{ marginTop: 8 }} />
          </div>
          <div className="skeleton-card__footer">
            <SkeletonLoader width={100} height={36} borderRadius={18} />
            <SkeletonLoader width={100} height={36} borderRadius={18} />
          </div>
        </div>
      );

    case 'stylist':
      return (
        <div className={`skeleton-card ${className}`}>
          <SkeletonLoader width="100%" height={200} borderRadius={8} />
          <div className="skeleton-card__stylist-info">
            <SkeletonLoader width="70%" height={20} style={{ marginTop: 12 }} />
            <SkeletonLoader width="50%" height={14} style={{ marginTop: 8 }} />
            <div className="skeleton-card__rating">
              <SkeletonLoader width={80} height={16} style={{ marginTop: 8 }} />
              <SkeletonLoader width={60} height={16} style={{ marginTop: 8 }} />
            </div>
          </div>
        </div>
      );

    case 'service':
      return (
        <div className={`skeleton-card skeleton-card--horizontal ${className}`}>
          <SkeletonLoader width={100} height={100} borderRadius={8} />
          <div className="skeleton-card__service-info">
            <SkeletonLoader width="90%" height={18} />
            <SkeletonLoader width="70%" height={14} style={{ marginTop: 8 }} />
            <SkeletonLoader width="50%" height={16} style={{ marginTop: 8 }} />
          </div>
        </div>
      );

    case 'notification':
      return (
        <div className={`skeleton-card skeleton-card--notification ${className}`}>
          <SkeletonLoader width={40} height={40} borderRadius="50%" />
          <div className="skeleton-card__notification-text">
            <SkeletonLoader width="80%" height={16} />
            <SkeletonLoader width="90%" height={14} style={{ marginTop: 6 }} />
            <SkeletonLoader width="40%" height={12} style={{ marginTop: 6 }} />
          </div>
        </div>
      );

    case 'generic':
    default:
      return (
        <div className={`skeleton-card ${className}`}>
          <SkeletonLoader width="100%" height={16} />
          <SkeletonLoader width="90%" height={16} style={{ marginTop: 8 }} />
          <SkeletonLoader width="70%" height={16} style={{ marginTop: 8 }} />
        </div>
      );
  }
};

/**
 * Skeleton List - Shows multiple skeleton cards
 */
interface SkeletonListProps {
  count?: number;
  type?: 'appointment' | 'stylist' | 'service' | 'notification' | 'generic';
  className?: string;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 5,
  type = 'generic',
  className = '',
}) => {
  return (
    <div className={`skeleton-list ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={`skeleton-${index}`} type={type} />
      ))}
    </div>
  );
};

export default SkeletonCard;

